import {
  Annotation,
  ChangeSet,
  type ChangeSpec,
  EditorState,
  type Extension,
  type RangeSet,
  RangeSetBuilder,
  RangeValue,
  StateField,
} from '@codemirror/state'
import {EditorView} from '@codemirror/view'
import type {Nodes} from 'mdast'
import {visit} from 'unist-util-visit'

import {getNodesContainingRange} from '../../markdown/node-range-utils'
import {isPositionedNode} from '../../markdown/positioned-node'
import {getMarkdownTree, markdownParserExtension, markdownTreeChanged} from './markdown-parser'
import {createText} from './single-line-editor'

declare module '@codemirror/state' {
  interface Transaction {
    // Missing from the type definitions but necessary for preserving annotations in transaction filters
    /** Annotations defined on this transaction. */
    readonly annotations: Array<Annotation<unknown>>
  }
}

// --- Atomic ranges --- //

/**
 * `AtomicRangeValue` is just a simple `RangeValue` instance since `RangeValue` itself is abstract. Atomic ranges have
 * no metadata associated with them so there's no need to construct a whole `Decoration`.
 */
class AtomicRangeValue extends RangeValue {
  override eq() {
    return true
  }
}

/**
 * Build atomic ranges for Markdown delimiters from the editor state. 'Atomic ranges' are ranges of text that are
 * treated as a single character in the document. Selection skips over them, and attempting to delete them deletes the
 * entire range at once.
 *
 * All delimiters that are always hidden (`.hiddenDelimiter` class) should be atomic ranges.
 *
 * This method automatically combines adjacent atomic ranges into a single range.
 * @see https://codemirror.net/docs/ref/#view.EditorView^atomicRanges
 */
function buildAtomicRanges(state: EditorState) {
  const tree = getMarkdownTree(state)

  const positions: Array<[start: number, to: number]> = []

  visit(tree, 'delimiter', delimiter => {
    if (isPositionedNode(delimiter) && delimiter.tokenType === 'blockQuotePrefix') {
      const start = delimiter.position.start.offset
      const end = delimiter.position.end.offset
      const lastPosition = positions.at(-1)

      if (lastPosition?.[1] === start) {
        // Combine adjacent ranges
        lastPosition[1] = end
      } else {
        // Otherwise create a new range, expanding backwards to include preceding newline
        positions.push([Math.max(start - 1, 0), end])
      }
    }
  })

  const builder = new RangeSetBuilder<AtomicRangeValue>()
  for (const [start, end] of positions) builder.add(start, end, new AtomicRangeValue())
  return builder.finish()
}

/** State field to provide atomic ranges to the editor. */
const atomicRangesState = StateField.define<RangeSet<AtomicRangeValue>>({
  create: buildAtomicRanges,
  update: (prev, tr) => (markdownTreeChanged(tr) ? buildAtomicRanges(tr.state) : prev),
  provide: field => [markdownParserExtension, EditorView.atomicRanges.from(field, s => () => s)],
})

// --- Transaction filters --- //

/** Applies the desired `depth` of blockquote delimiters to each line of the `text`. */
function applyBlockquoteDelimiters(text: string, depth: number) {
  return text.replaceAll('\n', `\n${'> '.repeat(depth)}`)
}

/** Returns all the `blockquote` nodes that contain this range. */
function getContainingBlockquotes(tree: Nodes, from: number, to: number) {
  return Array.from(getNodesContainingRange(tree, from, to)).filter(n => n.type === 'blockquote')
}

/** Annotation to apply to a transaction to indicate that blockquote continuation logic should be skipped. */
export const skipBlockquoteContinuation = Annotation.define<true>()

/**
 * Transaction filter that maintains the current blockquote level by prepending each line of the inserted text with
 * the correct number of blockquote delimiters. Will not trigger on transactions annotated by `skipBlockquoteContinuation`.
 *
 * Using a transaction filter for this (rather than a keybinding) allows us to process pasted, dropped, or otherwise
 * inserted text in addition to simple enter key presses.
 */
const blockquoteContinuationFilter = EditorState.transactionFilter.of(tr => {
  // Transaction filters are blocking operations, so we early-exit them as fast as possible if we can
  if (!tr.isUserEvent('input') || !tr.docChanged || tr.changes.empty || tr.annotation(skipBlockquoteContinuation))
    return tr

  const startTree = getMarkdownTree(tr.startState)

  let modified = false
  const mappedChanges: ChangeSpec[] = []
  tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
    const text = inserted.toString()

    // Avoid node tree searching if the change has no newlines
    if (!text.includes('\n')) return mappedChanges.push({from: fromA, to: toA, insert: inserted})

    const blockquoteDepth = getContainingBlockquotes(startTree, fromA, toA).length
    // No changes outside of blockquotes
    if (blockquoteDepth === 0) return mappedChanges.push({from: fromA, to: toA, insert: inserted})

    const quotedText = applyBlockquoteDelimiters(text, blockquoteDepth)

    mappedChanges.push({
      from: fromA,
      to: toA,
      insert: createText(quotedText),
    })
    modified = true
  })

  if (!modified) return tr

  const mappedChangeSet = ChangeSet.of(mappedChanges, tr.startState.doc.length)

  return [
    {
      changes: mappedChangeSet,
      // We must remap the start selection (rather than the new `tr.selection`) because we changed the length of the
      // inserted text. If there are multiple changes, any early change woud break the selection for all the
      // following changes
      // Setting assoc=1 causes the cursor to be associated with inserted text (text following it), thus ending up after it
      selection: tr.startState.selection?.map(mappedChangeSet, 1),
      effect: tr.effects,
      annotations: [skipBlockquoteContinuation.of(true), ...tr.annotations],
      userEvent: 'input',
      scrollIntoView: true,
    },
  ]
})

/**
 * Modifies the editor to apply Markdown-editing behaviors such as automatic continuation of blockquotes.
 */
export const markdownBehaviorsExtension: Extension = [
  atomicRangesState,
  markdownParserExtension,
  blockquoteContinuationFilter,
]
